(() => {
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('cyril-home-hero')) {
    class CyrilHomeHero extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        // Fill the first screen exactly: height = viewport minus whatever sits above the hero.
        this.controller = new AbortController();
        const { signal } = this.controller;
        const fit = () => this.style.setProperty('--ch-hero-top', `${Math.max(0, Math.floor(this.getBoundingClientRect().top + window.scrollY))}px`);
        fit();
        // Phones fire resize while the address bar slides; measure at most once per frame.
        let fitFrame = 0;
        window.addEventListener('resize', () => {
          cancelAnimationFrame(fitFrame);
          fitFrame = requestAnimationFrame(fit);
        }, { signal });
        // Slides can be limited to desktop or phones; only the ones for this screen take part.
        const phone = matchMedia('(max-width: 749px)');
        const collect = () => {
          const skip = phone.matches ? 'ch-hero__slide--desktop-only' : 'ch-hero__slide--mobile-only';
          this.slides = [...this.querySelectorAll('[data-ch-slide]')].filter((el) => !el.classList.contains(skip));
          this.bars = [...this.querySelectorAll('[data-ch-dot]')].filter((el) => !el.classList.contains(skip));
        };
        collect();
        phone.addEventListener('change', () => {
          collect();
          this.go(0, true);
        }, { signal });
        this.current = this.querySelector('[data-ch-current]');
        this.index = 0;
        this.delay = Number(this.dataset.autoplay) || 6000;
        this.visible = true;
        if (this.slides.length < 2) return;

        // Autoplay only while the poster is on screen; coming back restarts the current slide.
        this.observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting === this.visible) return;
          this.visible = entry.isIntersecting;
          if (this.visible) this.go(this.index, true);
          else this.stop();
        });
        this.observer.observe(this);

        [...this.querySelectorAll('[data-ch-dot]')].forEach((bar) => {
          bar.addEventListener('click', () => this.go(this.bars.indexOf(bar), true), { signal });
        });

        let startX = 0;
        let startY = 0;
        this.addEventListener('touchstart', (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        }, { passive: true, signal });
        this.addEventListener('touchend', (event) => {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          this.go(this.index + (dx < 0 ? 1 : -1), true);
        }, { passive: true, signal });

        document.addEventListener('visibilitychange', () => (document.hidden || !this.visible ? this.stop() : this.play()), { signal });
        this.play();
      }

      disconnectedCallback() {
        this.stop();
        this.controller?.abort();
        this.observer?.disconnect();
        this.started = false;
      }

      go(next, fromUser = false) {
        const all = this.querySelectorAll('[data-ch-slide]');
        const count = this.slides.length;
        this.index = (next + count) % count;
        all.forEach((slide) => {
          const active = slide === this.slides[this.index];
          slide.classList.toggle('is-active', active);
          slide.toggleAttribute('aria-hidden', !active);
          slide.inert = !active;
        });
        this.querySelectorAll('[data-ch-dot]').forEach((bar) => {
          bar.classList.remove('is-active');
          if (bar === this.bars[this.index]) {
            void bar.offsetWidth;
            bar.classList.add('is-active');
          }
        });
        if (this.current) this.current.textContent = String(this.index + 1).padStart(2, '0');
        if (fromUser) this.play();
      }

      play() {
        this.stop();
        if (reduceMotion()) {
          this.classList.add('is-static');
          return;
        }
        this.timer = setInterval(() => this.go(this.index + 1), this.delay);
      }

      stop() {
        clearInterval(this.timer);
      }
    }
    customElements.define('cyril-home-hero', CyrilHomeHero);
  }

  if (!customElements.get('cyril-scene-list')) {
    class CyrilSceneList extends HTMLElement {
      connectedCallback() {
        if (this.started || !this.hasAttribute('data-collapsible')) return;
        this.started = true;
        this.more = this.querySelector('[data-ch-more]');
        this.panel = this.querySelector('[data-ch-more-panel]');
        this.less = this.querySelector('[data-ch-less]');
        this.panel.inert = true;
        this.more.addEventListener('click', () => this.toggle(true));
        this.less.addEventListener('click', () => this.toggle(false));
      }

      toggle(open) {
        if (!open) {
          // Collapse in place: no long smooth scroll, just land on the peek and fade it in.
          this.classList.add('is-closing');
          this.classList.remove('is-open');
          const top = this.more.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({ top: Math.max(0, top - window.innerHeight * 0.45), behavior: 'instant' });
          requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-closing')));
        } else {
          this.classList.add('is-open');
        }
        this.more.setAttribute('aria-expanded', String(open));
        this.panel.inert = !open;
      }
    }
    customElements.define('cyril-scene-list', CyrilSceneList);
  }

  // Shared looping slider: a flex track moved by translateX, with a cloned first slide
  // at the end so the loop always travels forward. Autoplays only while on screen.
  class CyrilLoopSlider extends HTMLElement {
    setup(track, delay) {
      this.track = track;
      this.slides = [...track.children];
      if (this.slides.length < 2) return false;
      this.delay = delay;
      this.index = 0;
      this.visible = false;
      this.paused = false;
      const clone = this.slides[0].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-ch-scene-slide');
      clone.querySelector('img')?.setAttribute('alt', '');
      track.append(clone);

      this.controller = new AbortController();
      document.addEventListener('visibilitychange', () => this.sync(), { signal: this.controller.signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      });
      this.observer.observe(this);
      return true;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearInterval(this.timer);
      clearTimeout(this.resetTimer);
    }

    sync() {
      const run = this.visible && !this.paused && !document.hidden;
      if (run && !this.timer) {
        this.timer = setInterval(() => this.step(1), this.delay);
      } else if (!run && this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    // Manual input restarts the countdown so the next auto step waits a full interval.
    restart() {
      clearInterval(this.timer);
      this.timer = null;
      this.sync();
    }

    step(direction) {
      const count = this.slides.length;
      clearTimeout(this.resetTimer);
      if (this.index >= count) this.place(0);
      if (direction < 0 && this.index === 0) this.place(count);
      this.goTo(this.index + direction);
    }

    goTo(target) {
      const count = this.slides.length;
      const instant = reduceMotion();
      this.index = target;
      this.move(target, instant);
      if (target === count) {
        this.resetTimer = setTimeout(() => this.place(0), instant ? 0 : 760);
      }
      this.changed(target % count);
    }

    place(index) {
      this.index = index;
      this.move(index, true);
    }

    move(index, instant) {
      this.track.classList.toggle('is-instant', instant);
      this.track.style.transform = `translateX(${index * -100}%)`;
      if (instant) void this.track.offsetWidth;
    }

    changed() {}
  }

  if (!customElements.get('cyril-scene-colors')) {
    // Scene card helmet: one slide per colorway; arrows step manually.
    class CyrilSceneColors extends CyrilLoopSlider {
      connectedCallback() {
        if (this.started) return;
        const track = this.querySelector('[data-ch-track]');
        this.started = this.setup(track, Number(this.dataset.interval) || 3000);
        if (!this.started) return;
        this.link = this.closest('[data-ch-product-link]');
        const { signal } = this.controller;
        this.link?.addEventListener('pointerenter', () => { this.paused = true; this.sync(); }, { signal });
        this.link?.addEventListener('pointerleave', () => { this.paused = false; this.sync(); }, { signal });
        [['[data-ch-color-prev]', -1], ['[data-ch-color-next]', 1]].forEach(([selector, direction]) => {
          const arrow = this.querySelector(selector);
          const press = (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.step(direction);
            this.restart();
          };
          arrow?.addEventListener('click', press, { signal });
          arrow?.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') press(event);
          }, { signal });
        });
        // Touch: swipe across the helmet to change colorway; the swipe never opens the product.
        let startX = 0;
        let startY = 0;
        let swiped = false;
        this.addEventListener('touchstart', (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          swiped = false;
        }, { passive: true, signal });
        this.addEventListener('touchend', (event) => {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 24 || Math.abs(dx) < Math.abs(dy)) return;
          swiped = true;
          this.step(dx < 0 ? 1 : -1);
          this.restart();
        }, { passive: true, signal });
        this.link?.addEventListener('click', (event) => {
          if (!swiped) return;
          swiped = false;
          event.preventDefault();
        }, { signal });
      }

      changed(index) {
        const url = this.slides[index].dataset.url;
        if (this.link && url) this.link.href = url;
      }
    }
    customElements.define('cyril-scene-colors', CyrilSceneColors);
  }

  if (!customElements.get('cyril-scene-gallery')) {
    // Scene background photos: arrows, indicators and swipe; progress bar shows the countdown.
    class CyrilSceneGallery extends CyrilLoopSlider {
      connectedCallback() {
        if (this.started) return;
        const mobile = matchMedia('(max-width: 749px)').matches;
        const delay = Number(mobile ? this.dataset.intervalMobile : this.dataset.interval) || 5000;
        this.style.setProperty('--ch-scene-delay', `${delay}ms`);
        this.started = this.setup(this.querySelector('[data-ch-scene-track]'), delay);
        if (!this.started) return;
        this.dots = [...this.querySelectorAll('[data-ch-scene-dot]')];
        const { signal } = this.controller;
        const manual = (fn) => () => { fn(); this.restart(); };
        this.querySelector('[data-ch-scene-prev]')?.addEventListener('click', manual(() => this.step(-1)), { signal });
        this.querySelector('[data-ch-scene-next]')?.addEventListener('click', manual(() => this.step(1)), { signal });
        this.dots.forEach((dot) => {
          dot.addEventListener('click', manual(() => {
            const target = Number(dot.dataset.chSceneDot);
            const current = this.index % this.slides.length;
            if (target === current) return;
            if (this.index >= this.slides.length) this.place(0);
            this.goTo(target);
          }), { signal });
        });

        const stage = this.closest('.ch-scene__stage') || this;
        stage.addEventListener('cyril:video-open', () => { this.paused = true; this.sync(); }, { signal });
        stage.addEventListener('cyril:video-close', () => { this.paused = false; this.sync(); }, { signal });
        let startX = 0;
        let startY = 0;
        stage.addEventListener('touchstart', (event) => {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        }, { passive: true, signal });
        stage.addEventListener('touchend', (event) => {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - startY;
          if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
          this.step(dx < 0 ? 1 : -1);
          this.restart();
        }, { passive: true, signal });
        this.changed(0);
      }

      sync() {
        super.sync();
        this.classList.toggle('is-running', Boolean(this.timer));
      }

      restart() {
        super.restart();
        this.changed(this.index % this.slides.length);
      }

      changed(index) {
        this.dots?.forEach((dot, i) => {
          dot.classList.remove('is-active');
          if (i === index) {
            void dot.offsetWidth;
            dot.classList.add('is-active');
          }
        });
      }
    }
    customElements.define('cyril-scene-gallery', CyrilSceneGallery);
  }

  if (!customElements.get('cyril-welcome-popup')) {
    const STORAGE_KEY = 'cyril-welcome-seen-v1';

    class CyrilWelcomePopup extends HTMLElement {
      connectedCallback() {
        if (this.started) return;
        this.started = true;
        this.dialog = this.querySelector('dialog');
        this.controller = new AbortController();
        const { signal } = this.controller;

        this.querySelectorAll('[data-ch-close], [data-ch-choice]').forEach((button) => {
          // Choices only close for now: the guided shopping pages are not designed yet.
          button.addEventListener('click', () => this.close(), { signal });
        });
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        }, { signal });
        this.dialog.addEventListener('close', () => document.documentElement.classList.remove('ch-welcome-open'), { signal });

        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (this.contains(event.target) || event.target.contains(this)) this.open();
          }, { signal });
          document.addEventListener('shopify:section:deselect', () => this.close(), { signal });
          return;
        }

        if (this.dataset.enabled !== 'true') return;
        const forced = new URLSearchParams(location.search).has('welcome');
        if (!forced && this.seen()) return;
        this.timer = setTimeout(() => this.open(), Number(this.dataset.delay) || 0);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        this.controller?.abort();
        this.started = false;
      }

      seen() {
        try {
          return sessionStorage.getItem(STORAGE_KEY) === '1';
        } catch (error) {
          return false;
        }
      }

      open() {
        if (this.dialog.open) return;
        try {
          sessionStorage.setItem(STORAGE_KEY, '1');
        } catch (error) {
          /* storage unavailable: the popup simply shows again next visit */
        }
        document.documentElement.classList.add('ch-welcome-open');
        this.dialog.showModal();
        // Focus the panel instead of the close button so no focus ring shows on open.
        this.dialog.querySelector('.ch-welcome__panel')?.focus({ preventScroll: true });
      }

      close() {
        if (this.dialog.open) this.dialog.close();
      }
    }
    customElements.define('cyril-welcome-popup', CyrilWelcomePopup);
  }

  if (!customElements.get('cyril-ride-banner')) {
    // Scenes title band: play the black-to-white sweep each time it reaches the upper half of the screen
    // (reset once fully off screen), run the airflow only while visible.
    class CyrilRideBanner extends HTMLElement {
      connectedCallback() {
        if (this.observers) return;
        if (!('IntersectionObserver' in window)) {
          this.classList.add('is-revealed', 'is-live');
          return;
        }
        const center = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) this.classList.add('is-revealed');
        }, { rootMargin: '0px 0px -50% 0px' });
        const screen = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-live', entry.isIntersecting);
          if (!entry.isIntersecting) this.classList.remove('is-revealed');
        });
        center.observe(this);
        screen.observe(this);
        this.observers = [center, screen];
      }

      disconnectedCallback() {
        this.observers?.forEach((observer) => observer.disconnect());
        this.observers = null;
      }
    }
    customElements.define('cyril-ride-banner', CyrilRideBanner);
  }
})();
